/**
 * Cart Notes Component
 * 
 * Handles customer notes functionality for cart drawer and cart page.
 * 
 * Features:
 * - Accordion toggle with keyboard accessibility
 * - Live character counter
 * - Auto-save to cart.note via /cart/update.js
 * - Persists during section re-renders
 * - Progressive enhancement (works without JS)
 * 
 * Usage:
 * Include this module in pages that render the cart-notes.liquid snippet.
 * The component auto-initializes on DOMContentLoaded and section loads.
 */

class CartNotesManager {
  constructor(component) {
    this.component = component;
    this.context = component.dataset.context;
    this.toggle = component.querySelector('[data-cart-notes-toggle]');
    this.content = component.querySelector('[data-cart-notes-content]');
    this.input = component.querySelector('[data-cart-notes-input]');
    this.counter = component.querySelector('[data-cart-notes-counter]');
    this.currentCounter = this.counter.querySelector('[data-cart-notes-current]');
    this.limit = parseInt(this.counter.querySelector('[data-cart-notes-limit]').textContent);
    
    this.saveTimeout = null;
    this.saveDelay = 1000; // Debounce delay in ms
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCounter();
  }

  bindEvents() {
    // Accordion toggle
    this.toggle.addEventListener('click', this.handleToggle.bind(this));
    
    // Input events
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('blur', this.handleBlur.bind(this));
    
    // Keyboard accessibility for toggle
    this.toggle.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handle accordion toggle
   */
  handleToggle() {
    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open accordion
   */
  open() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.content.hidden = false;
    
    // Focus input after animation
    setTimeout(() => {
      this.input.focus();
    }, 200);
  }

  /**
   * Close accordion
   */
  close() {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.content.hidden = true;
  }

  /**
   * Handle input change
   */
  handleInput() {
    this.updateCounter();
    this.debouncedSave();
  }

  /**
   * Handle input blur (save immediately)
   */
  handleBlur() {
    // Clear debounce and save immediately
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.saveNote();
  }

  /**
   * Update character counter
   */
  updateCounter() {
    const currentLength = this.input.value.length;
    this.currentCounter.textContent = currentLength;
    
    // Visual feedback when approaching limit
    if (currentLength >= this.limit - 5) {
      this.counter.classList.add('text-red-600', 'font-semibold');
    } else {
      this.counter.classList.remove('text-red-600', 'font-semibold');
    }
  }

  /**
   * Debounced save
   */
  debouncedSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    this.saveTimeout = setTimeout(() => {
      this.saveNote();
    }, this.saveDelay);
  }

  /**
   * Save note to cart
   */
  async saveNote() {
    const note = this.input.value.trim();
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          note: note
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart note');
      }
      
      const cart = await response.json();
      
      // Dispatch event for other components (optional)
      const event = new CustomEvent('cart:note-updated', {
        detail: { note: cart.note, cart }
      });
      window.dispatchEvent(event);
        
    } catch (error) {
      console.error('Error saving cart note:', error);
      // Silently fail - note is still in the input
    }
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleToggle();
    }
  }
}

/**
 * Initialize all cart notes components
 */
function initCartNotes() {
  const components = document.querySelectorAll('[data-cart-notes]');
  
  components.forEach(component => {
    // Check if already initialized
    if (component.dataset.initialized === 'true') return;
    
    new CartNotesManager(component);
    component.dataset.initialized = 'true';
  });
}

// Auto-initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartNotes);
} else {
  initCartNotes();
}

// Re-initialize on Shopify section load (theme editor + section rendering)
document.addEventListener('shopify:section:load', initCartNotes);

// Export for use by other components
window.initCartNotes = initCartNotes;
